import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GreatLeapLogoIcon } from '../components/GreatLeapLogoIcon';

export default function Landing() {
  const navRef = useRef(null);

  /* ── SEO ── */
  useEffect(() => {
    document.title = 'GreatLeap — Performance & HR Platform for Ambitious Teams';

    const setMeta = (attrs, content) => {
      const sel = Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join('');
      let el = document.querySelector(`meta${sel}`);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const DESC = 'GreatLeap is the all-in-one performance and HR platform. Goals, OKRs, task management, performance reviews, and culture tools — all connected in one workspace.';
    const TITLE = 'GreatLeap — Performance & HR Platform for Ambitious Teams';

    setMeta({ name: 'description' }, DESC);
    setMeta({ name: 'keywords' }, 'HR platform, performance management, OKR tracking, employee performance, team management, HR software India, goals management, performance reviews');
    setMeta({ name: 'robots' }, 'index, follow');
    setMeta({ name: 'author' }, 'GreatLeap');

    setMeta({ property: 'og:title' }, TITLE);
    setMeta({ property: 'og:description' }, DESC);
    setMeta({ property: 'og:type' }, 'website');
    setMeta({ property: 'og:url' }, 'https://greatleap.tech');
    setMeta({ property: 'og:site_name' }, 'GreatLeap');

    setMeta({ name: 'twitter:card' }, 'summary_large_image');
    setMeta({ name: 'twitter:title' }, TITLE);
    setMeta({ name: 'twitter:description' }, DESC);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://greatleap.tech');

    return () => { document.title = 'Great Leap'; };
  }, []);

  /* ── Scroll nav shadow ── */
  useEffect(() => {
    const onScroll = () => {
      if (navRef.current) navRef.current.classList.toggle('gl-scrolled', window.scrollY > 20);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Scroll-in animations ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.style.opacity = '1';
          e.target.style.transform = 'translateY(0)';
        }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.gl-animate').forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition = 'opacity 0.55s ease, transform 0.55s ease';
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{CSS}</style>

      {/* JSON-LD structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "name": "GreatLeap",
            "url": "https://greatleap.tech",
            "description": "Performance and HR platform for ambitious teams",
            "foundingLocation": "India"
          },
          {
            "@type": "SoftwareApplication",
            "name": "GreatLeap",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "All-in-one performance and HR platform with goals, OKRs, task management, reviews, and culture tools",
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "0",
              "highPrice": "2499",
              "priceCurrency": "INR"
            }
          }
        ]
      })}} />

      {/* ── NAV ── */}
      <nav className="gl-nav" ref={navRef} role="navigation" aria-label="Main navigation">
        <div className="gl-nav-logo">
          <GreatLeapLogoIcon size={48} variant="light" />
        </div>
        <div className="gl-nav-links">
          <a href="#why">Why Us</a>
          <a href="#platform">Platform</a>
          <a href="#results">Results</a>
          <a href="#pricing">Pricing</a>
        </div>
        <div className="gl-nav-actions">
          <Link to="/signin"><button className="gl-btn-ghost">Sign In</button></Link>
          <Link to="/signup"><button className="gl-btn-primary">Start Free →</button></Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="gl-hero" aria-label="Hero">
        <div className="gl-hero-orb gl-orb1" aria-hidden="true" />
        <div className="gl-hero-orb gl-orb2" aria-hidden="true" />
        <div className="gl-hero-orb gl-orb3" aria-hidden="true" />

        <div className="gl-hero-pill" role="note">
          <span className="gl-dot" aria-hidden="true" />
          The performance platform built for ambitious teams
        </div>

        <h1 className="gl-hero-h1">
          Unlock Your Team's<br />
          <span className="gl-hero-gradient">Full Potential</span>
        </h1>

        <p className="gl-hero-sub">
          The platform brings together goals, performance, tasks, and culture
          into one intelligent platform — so every person performs at their best, every day.
        </p>

        <div className="gl-hero-actions">
          <Link to="/signup">
            <button className="gl-btn-hero-primary">
              Get Started Free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          </Link>
          <Link to="/signin">
            <button className="gl-btn-hero-secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
              Sign In to Dashboard
            </button>
          </Link>
        </div>

        <div className="gl-hero-trust" aria-label="Social proof">
          <div className="gl-trust-avatars" aria-hidden="true">
            {['A','R','S','P','K'].map((l, i) => (
              <span key={l} style={{ background: AVATAR_COLORS[i] }}>{l}</span>
            ))}
          </div>
          <span>Trusted by <strong>growing teams</strong> who take performance seriously</span>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="gl-stats-bar" aria-label="Key statistics">
        <div className="gl-stats-inner">
          {STATS.map((s) => (
            <div className="gl-stat-item" key={s.label}>
              <div className="gl-stat-num">{s.num}</div>
              <div className="gl-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHY ── */}
      <section className="gl-section gl-light" id="why" aria-labelledby="why-heading">
        <div className="gl-container">
          <div className="gl-section-head gl-centered">
            <div className="gl-label">Why Us</div>
            <h2 className="gl-title" id="why-heading">Performance isn't a process.<br />It's a culture.</h2>
            <p className="gl-sub">Most tools track work. The platform drives outcomes — connecting individual effort to team goals to organisation results.</p>
          </div>
          <div className="gl-why-grid">
            {WHY_CARDS.map((c) => (
              <div className="gl-why-card gl-animate" key={c.title}>
                <div className="gl-why-icon" aria-hidden="true">{c.icon}</div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM ── */}
      <section className="gl-section gl-white" id="platform" aria-labelledby="platform-heading">
        <div className="gl-container">
          <div className="gl-section-head gl-centered">
            <div className="gl-label">The Platform</div>
            <h2 className="gl-title" id="platform-heading">Every dimension of<br />performance, covered</h2>
            <p className="gl-sub">Six interconnected modules that work together so nothing falls through the cracks.</p>
          </div>
          <div className="gl-platform-grid">
            {PLATFORM_MODULES.map((m) => (
              <div className="gl-plat-card gl-animate" key={m.title}>
                <div className="gl-plat-icon" style={{ background: m.iconBg }} aria-hidden="true">{m.icon}</div>
                <div className="gl-plat-body">
                  <h3>{m.title}</h3>
                  <p>{m.desc}</p>
                  <div className="gl-plat-tags">
                    {m.tags.map((t) => <span key={t} className="gl-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="gl-section gl-dark" id="how" aria-labelledby="how-heading">
        <div className="gl-container">
          <div className="gl-section-head gl-centered">
            <div className="gl-label gl-label-light">How It Works</div>
            <h2 className="gl-title gl-title-light" id="how-heading">From setup to results<br />in minutes</h2>
            <p className="gl-sub gl-sub-dark">No consultant needed. No months-long rollout. Just sign up and start driving performance.</p>
          </div>
          <div className="gl-steps">
            {STEPS.map((s, i) => (
              <div className="gl-step gl-animate" key={s.title}>
                <div className="gl-step-line">
                  <div className="gl-step-num">{i + 1}</div>
                  {i < STEPS.length - 1 && <div className="gl-step-connector" aria-hidden="true" />}
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FULL CAPABILITY ── */}
      <section className="gl-section gl-light" id="capability" aria-labelledby="capability-heading">
        <div className="gl-container">
          <div className="gl-section-head gl-centered">
            <div className="gl-label">Full Capability Suite</div>
            <h2 className="gl-title" id="capability-heading">Everything connected.<br />Nothing siloed.</h2>
            <p className="gl-sub">All the tools your team needs — all talking to each other.</p>
          </div>
          <div className="gl-cap-grid">
            {CAPABILITIES.map((c) => (
              <div className="gl-cap-card gl-animate" key={c.title}>
                <div className="gl-cap-icon" style={{ background: c.iconBg }} aria-hidden="true">{c.icon}</div>
                <div>
                  <h4>{c.title}</h4>
                  <p>{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RESULTS / TESTIMONIALS ── */}
      <section className="gl-section gl-white" id="results" aria-labelledby="results-heading">
        <div className="gl-container">
          <div className="gl-section-head gl-centered">
            <div className="gl-label">Real Results</div>
            <h2 className="gl-title" id="results-heading">Teams that leap forward</h2>
          </div>
          <div className="gl-testimonials">
            {TESTIMONIALS.map((t) => (
              <div className="gl-testi-card gl-animate" key={t.name}>
                <div className="gl-testi-stars" aria-label="5 out of 5 stars">★★★★★</div>
                <blockquote>"{t.quote}"</blockquote>
                <div className="gl-testi-author">
                  <div className="gl-testi-avatar" style={{ background: t.avatarBg }} aria-hidden="true">{t.initials}</div>
                  <div>
                    <div className="gl-testi-name">{t.name}</div>
                    <div className="gl-testi-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="gl-section gl-light" id="pricing" aria-labelledby="pricing-heading">
        <div className="gl-container">
          <div className="gl-section-head gl-centered">
            <div className="gl-label">Pricing</div>
            <h2 className="gl-title" id="pricing-heading">Invest in performance.<br />Not in complexity.</h2>
            <p className="gl-sub" style={{ margin: '0 auto' }}>Start free. No credit card. Scale as your team grows.</p>
          </div>
          <div className="gl-pricing-grid">
            {PLANS.map((plan) => (
              <div className={`gl-price-card gl-animate${plan.featured ? ' gl-featured' : ''}`} key={plan.name}>
                {plan.featured && <div className="gl-popular-badge">Most Popular</div>}
                <div className="gl-plan-name">{plan.name}</div>
                <div className="gl-plan-price">
                  <><sub>₹</sub>{plan.price}<small> {plan.priceNote}</small></>
                </div>
                <p className="gl-plan-desc">{plan.desc}</p>
                <div className="gl-plan-divider" />
                <ul className="gl-plan-features">
                  {plan.features.map((f) => (
                    <li key={f.text} className={f.included ? '' : 'gl-no'}>{f.text}</li>
                  ))}
                </ul>
                <Link to="/signup" style={{ display: 'block', textDecoration: 'none' }}>
                  <button className={`gl-btn-plan ${plan.featured ? 'gl-btn-plan-fill' : 'gl-btn-plan-outline'}`}>
                    {plan.cta}
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="gl-cta" aria-label="Call to action">
        <div className="gl-cta-orb" aria-hidden="true" />
        <div className="gl-container" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div className="gl-cta-pill">Start your performance journey today</div>
          <h2 className="gl-cta-h2">Your team is capable<br />of so much more.</h2>
          <p className="gl-cta-sub">Give them the platform that helps them prove it.</p>
          <div className="gl-cta-actions">
            <Link to="/signup"><button className="gl-btn-cta-white">Create Free Account →</button></Link>
            <Link to="/signin"><button className="gl-btn-cta-outline">Sign In to Dashboard</button></Link>
          </div>
          <p className="gl-cta-note">Free forever for small teams · No credit card required</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="gl-footer" role="contentinfo">
        <div className="gl-container">
          <div className="gl-footer-top">
            <div className="gl-footer-brand">
              <div className="gl-footer-logo" aria-label="GreatLeap" />
              <p>The performance platform for modern, ambitious teams. Goals, tasks, reviews, and culture — all in one place.</p>
              <div className="gl-footer-tagline">Performance. Culture. Growth.</div>
            </div>
            <div className="gl-footer-col">
              <h5>Platform</h5>
              <ul>
                <li><a href="#why">Why Us</a></li>
                <li><a href="#platform">Features</a></li>
                <li><a href="#capability">Capabilities</a></li>
                <li><a href="#pricing">Pricing</a></li>
              </ul>
            </div>
            <div className="gl-footer-col">
              <h5>Use Cases</h5>
              <ul>
                <li><a href="#platform">Team Performance</a></li>
                <li><a href="#platform">Goal Management</a></li>
                <li><a href="#platform">People Operations</a></li>
                <li><a href="#platform">Culture Building</a></li>
              </ul>
            </div>
            <div className="gl-footer-col">
              <h5>Account</h5>
              <ul>
                <li><Link to="/signup">Sign Up Free</Link></li>
                <li><Link to="/signin">Sign In</Link></li>
              </ul>
            </div>
          </div>
          <div className="gl-footer-bottom">
            <div>© 2026 · GreatLeap — The Performance Platform</div>
            <div className="gl-footer-links">
              <a href="#pricing">Privacy</a>
              <a href="#pricing">Terms</a>
              <a href="#pricing">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

/* ─── Data ─── */
const AVATAR_COLORS = [
  'linear-gradient(135deg,#2563eb,#0891b2)',
  'linear-gradient(135deg,#0891b2,#0d9488)',
  'linear-gradient(135deg,#7c3aed,#6d28d9)',
  'linear-gradient(135deg,#059669,#047857)',
  'linear-gradient(135deg,#db2777,#be185d)',
];

const STATS = [
  { num: '10+',  label: 'Performance Modules' },
  { num: '360°', label: 'Team Visibility' },
  { num: '3×',   label: 'Faster Review Cycles' },
  { num: '100%', label: 'Workspace Data Isolation' },
];

const WHY_CARDS = [
  {
    icon: '🎯',
    title: 'Goals that actually get done',
    desc: 'Connect individual goals to team OKRs and org strategy. Everyone knows what winning looks like — and tracks progress in real time.',
  },
  {
    icon: '⚡',
    title: 'Performance at the speed of work',
    desc: "Continuous feedback, instant @mentions, and live task timelines replace the annual review cycle with always-on performance conversations.",
  },
  {
    icon: '🔗',
    title: 'Everything connected',
    desc: 'Tasks link to roles. Reviews link to goals. Skills link to growth plans. No data silos, no copy-paste, no missed context.',
  },
  {
    icon: '👁',
    title: 'Full leadership visibility',
    desc: 'Manager dashboards give real-time insight into team health, task progress, time allocation, and performance trends — without micromanaging.',
  },
  {
    icon: '🌱',
    title: 'Culture you can measure',
    desc: 'Appreciations, feedback, and social recognition are built into the daily workflow — not bolted on as afterthoughts.',
  },
  {
    icon: '🚀',
    title: 'Scales with your ambition',
    desc: 'From 5-person startups to 500-person enterprises — one workspace per team, unlimited workspaces per account.',
  },
];

const PLATFORM_MODULES = [
  {
    icon: '📊', iconBg: 'rgba(8,145,178,0.10)',
    title: 'Performance Reviews',
    desc: 'Run structured review cycles with multi-dimensional ratings against role performance areas. Appraisals, ratings, and insights — all automated.',
    tags: ['Review Cycles', 'Appraisals', 'Ratings'],
  },
  {
    icon: '🎯', iconBg: 'rgba(21,145,91,0.10)',
    title: 'Goals & OKRs',
    desc: 'Set individual and team goals with manager approval flows. Track progress, measure achievement, and celebrate wins together.',
    tags: ['Goal Setting', 'Approval Flow', 'Progress Tracking'],
  },
  {
    icon: '✅', iconBg: 'rgba(79,95,229,0.10)',
    title: 'Task & Project Execution',
    desc: 'Assign tasks, track time, manage checklists, and review quality — with a full Gantt chart and an immutable timeline for every change made.',
    tags: ['Gantt', 'Time Logs', 'Quality Reviews'],
  },
  {
    icon: '🌱', iconBg: 'rgba(194,120,10,0.10)',
    title: 'Culture & Recognition',
    desc: "Rich-text social feed with appreciations, feedback, and reactions. @mention teammates and celebrate wins where the whole team can see.",
    tags: ['Social Feed', 'Appreciations', '@Mentions'],
  },
  {
    icon: '🏗', iconBg: 'rgba(212,72,72,0.10)',
    title: 'People & Org Structure',
    desc: 'Map your full org hierarchy — departments, branches, roles, and reporting lines. Interactive org chart with printable role profiles.',
    tags: ['Org Chart', 'Role Profiles', 'Skills Matrix'],
  },
  {
    icon: '📈', iconBg: 'rgba(13,148,136,0.10)',
    title: 'Dashboards & Analytics',
    desc: 'Personal, team, and manager dashboards give the right view to the right person. Time summaries, performance trends, task velocity.',
    tags: ['Manager View', 'Team Stats', 'Time Summary'],
  },
];

const STEPS = [
  {
    title: 'Create your workspace',
    desc: 'Sign up in 30 seconds. Create your workspace and invite your team by email. New members join automatically on first sign-in.',
  },
  {
    title: 'Define roles & goals',
    desc: 'Build your org structure, create role profiles with performance dimensions, and set team goals aligned to your strategy.',
  },
  {
    title: 'Run the performance cycle',
    desc: 'Assign tasks, track time, give feedback, run reviews, and celebrate achievement — all from one connected platform.',
  },
];

const CAPABILITIES = [
  { icon: '📍', iconBg: 'rgba(8,145,178,0.10)',   title: 'Locations & Branches',  desc: 'Multi-site org mapping with timezone support.' },
  { icon: '🎓', iconBg: 'rgba(21,145,91,0.10)',   title: 'Skills & Competencies', desc: 'Role-skill mapping with proficiency levels 1–5.' },
  { icon: '📋', iconBg: 'rgba(194,120,10,0.10)',  title: 'Policies & Compliance',  desc: 'Versioned policy documents with status tracking.' },
  { icon: '⏱',  iconBg: 'rgba(124,58,237,0.10)',  title: 'Time Tracking',          desc: 'Log hours by range or timer. Per-user breakdowns.' },
  { icon: '🔔', iconBg: 'rgba(212,72,72,0.10)',   title: 'Smart Notifications',    desc: 'Cross-workspace @mention alerts in one inbox.' },
  { icon: '🔍', iconBg: 'rgba(13,148,136,0.10)',  title: 'Quality Gates',          desc: 'Reviewer ratings and notes on every deliverable.' },
  { icon: '🔒', iconBg: 'rgba(79,95,229,0.10)',   title: 'Role-Based Access',      desc: 'Super Admin, Admin, Member — granular permissions.' },
  { icon: '📁', iconBg: 'rgba(249,115,22,0.10)',  title: 'Audit & History',        desc: 'Immutable task timelines and platform audit logs.' },
];

const TESTIMONIALS = [
  {
    quote: "We replaced our annual review with GreatLeap's continuous cycle. Performance conversations happen weekly now — not once a year. Engagement is up 40%.",
    name: 'Aditi Rao', role: 'Chief People Officer, TechNova India',
    initials: 'AR', avatarBg: 'linear-gradient(135deg,#2563eb,#0891b2)',
  },
  {
    quote: 'Goals used to live in spreadsheets nobody looked at. Now every team member knows their targets, tracks progress daily, and managers get live visibility. Game changer.',
    name: 'Suresh Pillai', role: 'COO, Meridian Group',
    initials: 'SP', avatarBg: 'linear-gradient(135deg,#0891b2,#0d9488)',
  },
  {
    quote: 'The appreciation feed changed how our team talks to each other. Recognition is public, feedback is structured, and culture has become something we can actually measure.',
    name: 'Priya Menon', role: 'VP People, CloudBridge',
    initials: 'PM', avatarBg: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
  },
];

const PLANS = [
  {
    name: 'Free', price: '0', priceNote: '/ mo',
    desc: 'Perfect for small teams getting started with performance management.',
    cta: 'Start Free',
    features: [
      { text: 'Up to 5 members', included: true },
      { text: 'Goals & task management', included: true },
      { text: 'Social feed & recognition', included: true },
      { text: 'Org structure & profiles', included: true },
      { text: 'Smart notifications', included: true },
      { text: 'Performance review cycles', included: false },
      { text: 'Advanced dashboards', included: false },
    ],
  },
  {
    name: 'Pro', price: '199', priceNote: '/ user / mo', featured: true,
    desc: 'Full performance platform for growing teams. Pay only for what you use.',
    cta: 'Get Started',
    features: [
      { text: 'Unlimited members', included: true },
      { text: 'Full goals & OKR tracking', included: true },
      { text: 'Performance review cycles', included: true },
      { text: 'Appraisals & ratings', included: true },
      { text: 'Task + Gantt + time logs', included: true },
      { text: 'Manager & team dashboards', included: true },
      { text: 'Quality gates & checklists', included: true },
    ],
  },
];

/* ─── Styles ─── */
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --accent:      #0891b2;
    --accent-lt:   #22d3ee;
    --teal:        #0d9488;
    --accent-bg:   rgba(8,145,178,0.08);
    --accent-bdr:  rgba(8,145,178,0.22);
    --brand-grad:  linear-gradient(120deg, #2563eb 0%, #0891b2 60%, #0d9488 100%);
    --dark:        #07090c;
    --mid:         #0b0e13;
    --dark2:       #111827;
    --gray:        #5a6472;
    --light:       #f6f6f4;
    --white:       #fff;
    --border:      #e2e2dd;
    --radius:      12px;
    --pos:         #15915b;
  }

  body {
    font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: #fff;
    color: var(--dark2);
    line-height: 1.6;
    overflow-x: hidden;
  }
  a { text-decoration: none; }

  /* ── NAV ── */
  .gl-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 52px; height: 64px;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(226,226,221,0.6);
    transition: box-shadow 0.2s;
  }
  .gl-nav.gl-scrolled { box-shadow: 0 2px 20px rgba(0,0,0,0.07); }
  .gl-nav-logo { display: flex; align-items: center; gap: 10px; }
  .gl-nav-links { display: flex; align-items: center; gap: 30px; }
  .gl-nav-links a { font-size: 14px; font-weight: 500; color: var(--gray); transition: color 0.15s; }
  .gl-nav-links a:hover { color: var(--dark2); }
  .gl-nav-actions { display: flex; align-items: center; gap: 10px; }
  .gl-nav-actions a { text-decoration: none; }

  .gl-btn-ghost {
    padding: 7px 16px; border-radius: 8px; font-size: 13.5px; font-weight: 600;
    color: var(--dark2); border: 1.5px solid var(--border);
    background: transparent; cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
  }
  .gl-btn-ghost:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }

  .gl-btn-primary {
    padding: 8px 18px; border-radius: 8px; font-size: 13.5px; font-weight: 700;
    color: #fff; background: var(--brand-grad); border: none; cursor: pointer;
    font-family: inherit;
    box-shadow: 0 2px 10px rgba(8,145,178,0.30);
    transition: all 0.15s;
  }
  .gl-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(8,145,178,0.38); }

  /* ── HERO ── */
  .gl-hero {
    min-height: 100vh; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 120px 24px 90px;
    position: relative; overflow: hidden; background: #fff;
  }
  .gl-hero-orb { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(80px); }
  .gl-orb1 { width: 680px; height: 680px; background: rgba(8,145,178,0.07); top: -200px; right: -180px; }
  .gl-orb2 { width: 480px; height: 480px; background: rgba(13,148,136,0.06); bottom: -140px; left: -90px; }
  .gl-orb3 { width: 280px; height: 280px; background: rgba(37,99,235,0.05); top: 30%; left: 20%; }

  .gl-hero-pill {
    position: relative; z-index: 1;
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--accent-bg); border: 1px solid var(--accent-bdr);
    padding: 7px 18px; border-radius: 999px;
    font-size: 12.5px; font-weight: 600; color: var(--accent);
    margin-bottom: 28px;
  }
  .gl-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--accent-lt);
    animation: gl-pulse 2s infinite;
  }
  @keyframes gl-pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }

  .gl-hero-h1 {
    position: relative; z-index: 1;
    font-size: clamp(46px, 7.5vw, 84px);
    font-weight: 900; letter-spacing: -3px; line-height: 1.06;
    color: var(--dark2); margin-bottom: 12px;
  }
  .gl-hero-gradient {
    background: var(--brand-grad);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .gl-hero-sub {
    position: relative; z-index: 1;
    font-size: clamp(17px, 2.5vw, 21px); color: var(--gray);
    max-width: 580px; margin: 18px auto 38px;
    line-height: 1.65; font-weight: 400;
  }
  .gl-hero-actions {
    position: relative; z-index: 1;
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap; justify-content: center;
  }
  .gl-hero-actions a { text-decoration: none; }

  .gl-btn-hero-primary {
    display: inline-flex; align-items: center; gap: 9px;
    padding: 15px 32px; border-radius: var(--radius);
    font-size: 15.5px; font-weight: 700; color: #fff;
    background: var(--brand-grad); border: none; cursor: pointer;
    font-family: inherit;
    box-shadow: 0 4px 20px rgba(8,145,178,0.35);
    transition: all 0.2s;
  }
  .gl-btn-hero-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(8,145,178,0.42); }

  .gl-btn-hero-secondary {
    display: inline-flex; align-items: center; gap: 9px;
    padding: 14px 32px; border-radius: var(--radius);
    font-size: 15.5px; font-weight: 700; color: var(--dark2);
    background: #fff; border: 2px solid var(--border); cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
  }
  .gl-btn-hero-secondary:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); transform: translateY(-2px); }

  .gl-hero-trust {
    position: relative; z-index: 1; margin-top: 56px;
    display: flex; align-items: center; justify-content: center; gap: 10px;
    font-size: 13px; color: var(--gray);
  }
  .gl-hero-trust strong { color: var(--dark2); }
  .gl-trust-avatars { display: flex; }
  .gl-trust-avatars span {
    width: 32px; height: 32px; border-radius: 50%; border: 2px solid #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; color: #fff; margin-left: -9px;
  }
  .gl-trust-avatars span:first-child { margin-left: 0; }

  /* ── STATS ── */
  .gl-stats-bar { background: var(--dark); }
  .gl-stats-inner {
    max-width: 1100px; margin: 0 auto;
    display: grid; grid-template-columns: repeat(4,1fr);
    border-left: 1px solid rgba(255,255,255,0.05);
  }
  .gl-stat-item {
    padding: 40px 32px; text-align: center;
    border-right: 1px solid rgba(255,255,255,0.05);
    border-top: 1px solid rgba(255,255,255,0.05);
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .gl-stat-num {
    font-size: 40px; font-weight: 900; letter-spacing: -1px; line-height: 1; margin-bottom: 8px;
    background: linear-gradient(120deg, #22d3ee 0%, #5eead4 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .gl-stat-label { font-size: 13px; color: rgba(255,255,255,0.45); font-weight: 500; }

  /* ── SECTION COMMON ── */
  .gl-section { padding: 96px 24px; }
  .gl-container { max-width: 1100px; margin: 0 auto; }
  .gl-light { background: var(--light); }
  .gl-white { background: #fff; }
  .gl-dark { background: var(--dark); }
  .gl-section-head { margin-bottom: 56px; }
  .gl-section-head.gl-centered { text-align: center; }

  .gl-label {
    display: inline-block; font-size: 11px; font-weight: 700;
    letter-spacing: 1.8px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 12px;
  }
  .gl-label-light { color: var(--accent-lt) !important; }

  .gl-title {
    font-size: clamp(28px,4vw,44px); font-weight: 900;
    color: var(--dark2); letter-spacing: -1.2px; line-height: 1.12; margin-bottom: 16px;
  }
  .gl-title-light { color: #f1f5f9 !important; }

  .gl-sub { font-size: 16.5px; color: var(--gray); max-width: 520px; line-height: 1.72; margin: 0 auto; }
  .gl-sub-dark { color: rgba(255,255,255,0.45) !important; margin: 0 auto; }

  /* ── WHY ── */
  .gl-why-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
  .gl-why-card {
    background: #fff; border-radius: var(--radius); padding: 28px;
    border: 1px solid var(--border); transition: all 0.22s;
  }
  .gl-why-card:hover { transform: translateY(-4px); box-shadow: 0 12px 36px rgba(0,0,0,0.07); border-color: var(--accent-bdr); }
  .gl-why-icon { font-size: 26px; margin-bottom: 14px; }
  .gl-why-card h3 { font-size: 15.5px; font-weight: 700; color: var(--dark2); margin-bottom: 9px; }
  .gl-why-card p { font-size: 13.5px; color: var(--gray); line-height: 1.7; }

  /* ── PLATFORM ── */
  .gl-platform-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
  .gl-plat-card {
    background: var(--light); border-radius: var(--radius); padding: 26px;
    border: 1px solid var(--border); transition: all 0.22s;
    display: flex; flex-direction: column; position: relative; overflow: hidden;
  }
  .gl-plat-card::after {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: var(--brand-grad); opacity: 0; transition: opacity 0.22s;
  }
  .gl-plat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 36px rgba(0,0,0,0.07); border-color: var(--accent-bdr); background: #fff; }
  .gl-plat-card:hover::after { opacity: 1; }
  .gl-plat-icon { width: 48px; height: 48px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 22px; margin-bottom: 16px; }
  .gl-plat-body h3 { font-size: 15.5px; font-weight: 700; color: var(--dark2); margin-bottom: 9px; }
  .gl-plat-body p { font-size: 13px; color: var(--gray); line-height: 1.68; margin-bottom: 14px; }
  .gl-plat-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .gl-tag {
    font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px;
    background: var(--accent-bg); color: var(--accent); border: 1px solid var(--accent-bdr);
  }

  /* ── STEPS ── */
  .gl-steps { display: grid; grid-template-columns: repeat(3,1fr); gap: 48px; }
  .gl-step { display: flex; flex-direction: column; align-items: center; text-align: center; }
  .gl-step-line { display: flex; align-items: center; margin-bottom: 22px; width: 100%; justify-content: center; position: relative; }
  .gl-step-num {
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--brand-grad);
    color: #fff; font-size: 22px; font-weight: 900;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 18px rgba(8,145,178,0.35);
    z-index: 1; flex-shrink: 0;
  }
  .gl-step h3 { font-size: 16px; font-weight: 700; color: #e2e8f0; margin-bottom: 10px; }
  .gl-step p { font-size: 13.5px; color: rgba(255,255,255,0.38); line-height: 1.7; }

  /* ── CAPABILITIES ── */
  .gl-cap-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
  .gl-cap-card {
    background: #fff; border-radius: var(--radius); padding: 20px;
    border: 1px solid var(--border); display: flex; gap: 14px;
    align-items: flex-start; transition: all 0.2s;
  }
  .gl-cap-card:hover { border-color: var(--accent-bdr); box-shadow: 0 6px 20px rgba(0,0,0,0.06); transform: translateY(-2px); }
  .gl-cap-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
  .gl-cap-card h4 { font-size: 13.5px; font-weight: 700; color: var(--dark2); margin-bottom: 4px; }
  .gl-cap-card p { font-size: 12px; color: var(--gray); line-height: 1.6; }

  /* ── TESTIMONIALS ── */
  .gl-testimonials { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
  .gl-testi-card {
    background: var(--light); border-radius: var(--radius); padding: 28px;
    border: 1px solid var(--border); transition: all 0.2s;
  }
  .gl-testi-card:hover { transform: translateY(-4px); box-shadow: 0 10px 32px rgba(0,0,0,0.07); border-color: var(--accent-bdr); background: #fff; }
  .gl-testi-stars { color: #d97706; font-size: 13px; margin-bottom: 14px; letter-spacing: 3px; }
  .gl-testi-card blockquote { font-size: 14px; color: #374151; line-height: 1.78; font-style: italic; margin-bottom: 20px; }
  .gl-testi-author { display: flex; align-items: center; gap: 12px; }
  .gl-testi-avatar { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; font-weight: 800; flex-shrink: 0; }
  .gl-testi-name { font-size: 13px; font-weight: 700; color: var(--dark2); }
  .gl-testi-role { font-size: 11.5px; color: var(--gray); }

  /* ── PRICING ── */
  .gl-pricing-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 32px; align-items: start; max-width: 800px; margin: 0 auto; }
  .gl-price-card {
    border-radius: var(--radius); padding: 32px 28px 36px;
    border: 2px solid var(--border); background: #fff; transition: all 0.2s;
  }
  .gl-price-card.gl-featured {
    border-color: var(--accent);
    background: linear-gradient(160deg, var(--accent-bg) 0%, #fff 50%);
    box-shadow: 0 10px 32px rgba(8,145,178,0.12);
    position: relative;
  }
  .gl-price-card:hover:not(.gl-featured) { border-color: var(--accent-bdr); box-shadow: 0 6px 20px rgba(0,0,0,0.07); }
  .gl-popular-badge {
    position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
    background: var(--brand-grad); color: #fff;
    font-size: 11px; font-weight: 700; padding: 4px 18px; border-radius: 20px;
    white-space: nowrap; letter-spacing: 0.3px;
  }
  .gl-plan-name { font-size: 11.5px; font-weight: 700; color: var(--gray); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 12px; }
  .gl-plan-price { font-size: 42px; font-weight: 900; color: var(--dark2); letter-spacing: -1.5px; line-height: 1; }
  .gl-plan-price sub { font-size: 18px; font-weight: 600; vertical-align: super; letter-spacing: 0; }
  .gl-plan-price small { font-size: 14px; font-weight: 500; color: var(--gray); letter-spacing: 0; }
  .gl-plan-desc { font-size: 13px; color: var(--gray); margin: 14px 0 24px; line-height: 1.65; }
  .gl-plan-divider { height: 1px; background: var(--border); margin-bottom: 22px; }
  .gl-plan-features { list-style: none; padding: 0; margin-bottom: 26px; }
  .gl-plan-features li {
    font-size: 13px; color: #374151; padding: 7px 0;
    display: flex; align-items: center; gap: 10px;
    border-bottom: 1px solid var(--light);
  }
  .gl-plan-features li:last-child { border-bottom: none; }
  .gl-plan-features li::before { content: '✓'; color: var(--pos); font-weight: 800; font-size: 12px; flex-shrink: 0; }
  .gl-plan-features li.gl-no { color: #9ca3af; }
  .gl-plan-features li.gl-no::before { content: '×'; color: #d1d5db; }

  .gl-btn-plan {
    width: 100%; padding: 13px; border-radius: 9px; font-size: 14.5px;
    font-weight: 700; cursor: pointer; transition: all 0.15s; border: none;
    font-family: inherit;
  }
  .gl-btn-plan-outline { background: transparent; color: var(--dark2); border: 2px solid var(--border); }
  .gl-btn-plan-outline:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }
  .gl-btn-plan-fill { background: var(--brand-grad); color: #fff; box-shadow: 0 4px 14px rgba(8,145,178,0.30); }
  .gl-btn-plan-fill:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(8,145,178,0.38); }

  /* ── CTA ── */
  .gl-cta {
    background: linear-gradient(135deg, var(--dark) 0%, #0a1628 40%, #0c2a40 80%, #0d3346 100%);
    padding: 108px 24px; text-align: center; position: relative; overflow: hidden;
  }
  .gl-cta-orb {
    position: absolute; top: -120px; right: -120px; width: 580px; height: 580px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(8,145,178,0.18) 0%, transparent 70%);
    pointer-events: none;
  }
  .gl-cta-pill {
    display: inline-flex; align-items: center;
    background: rgba(8,145,178,0.12); border: 1px solid rgba(8,145,178,0.25);
    padding: 6px 18px; border-radius: 999px;
    font-size: 12px; font-weight: 600; color: var(--accent-lt);
    margin-bottom: 22px; letter-spacing: 0.3px;
  }
  .gl-cta-h2 { font-size: clamp(32px, 5.5vw, 56px); font-weight: 900; color: #fff; letter-spacing: -1.5px; margin-bottom: 18px; line-height: 1.1; }
  .gl-cta-sub { font-size: 18px; color: rgba(34,211,238,0.7); margin-bottom: 42px; font-weight: 400; }
  .gl-cta-actions { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }
  .gl-cta-actions a { text-decoration: none; }
  .gl-btn-cta-white {
    padding: 15px 36px; border-radius: var(--radius); font-size: 15.5px; font-weight: 700;
    color: var(--accent); background: #fff; border: none; cursor: pointer; font-family: inherit;
    box-shadow: 0 6px 22px rgba(0,0,0,0.22); transition: all 0.2s;
  }
  .gl-btn-cta-white:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0,0,0,0.28); }
  .gl-btn-cta-outline {
    padding: 14px 36px; border-radius: var(--radius); font-size: 15.5px; font-weight: 700;
    color: #fff; background: transparent; border: 2px solid rgba(255,255,255,0.25);
    cursor: pointer; font-family: inherit; transition: all 0.2s;
  }
  .gl-btn-cta-outline:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.5); transform: translateY(-2px); }
  .gl-cta-note { margin-top: 20px; font-size: 12.5px; color: rgba(255,255,255,0.3); }

  /* ── FOOTER ── */
  .gl-footer { background: var(--dark); padding: 60px 24px 30px; color: rgba(255,255,255,0.4); }
  .gl-footer-top { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 52px; margin-bottom: 48px; }
  .gl-footer-logo { height: 28px; margin-bottom: 16px; }
  .gl-footer-brand p { font-size: 13px; line-height: 1.75; max-width: 240px; margin-bottom: 16px; }
  .gl-footer-tagline { font-size: 11px; font-weight: 700; color: var(--accent-lt); letter-spacing: 1.2px; text-transform: uppercase; }
  .gl-footer-col h5 { font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.75); margin-bottom: 16px; letter-spacing: 0.3px; }
  .gl-footer-col ul { list-style: none; padding: 0; }
  .gl-footer-col li { margin-bottom: 10px; }
  .gl-footer-col a { font-size: 13px; color: rgba(255,255,255,0.35); transition: color 0.15s; text-decoration: none; }
  .gl-footer-col a:hover { color: rgba(255,255,255,0.6); }
  .gl-footer-bottom {
    border-top: 1px solid rgba(255,255,255,0.06); padding-top: 24px;
    display: flex; align-items: center; justify-content: space-between;
    font-size: 12.5px; color: rgba(255,255,255,0.22);
  }
  .gl-footer-links { display: flex; gap: 20px; }
  .gl-footer-links a { color: rgba(255,255,255,0.22); transition: color 0.15s; text-decoration: none; }
  .gl-footer-links a:hover { color: rgba(255,255,255,0.42); }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .gl-cap-grid { grid-template-columns: repeat(2,1fr); }
    .gl-footer-top { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 900px) {
    .gl-nav { padding: 0 24px; }
    .gl-nav-links { display: none; }
    .gl-why-grid, .gl-platform-grid, .gl-steps, .gl-testimonials, .gl-pricing-grid { grid-template-columns: 1fr 1fr; }
    .gl-stats-inner { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 600px) {
    .gl-why-grid, .gl-platform-grid, .gl-steps, .gl-testimonials, .gl-pricing-grid, .gl-stats-inner, .gl-cap-grid { grid-template-columns: 1fr; }
    .gl-footer-top { grid-template-columns: 1fr; }
    .gl-hero-actions, .gl-cta-actions { flex-direction: column; align-items: center; }
    .gl-btn-hero-primary, .gl-btn-hero-secondary, .gl-btn-cta-white, .gl-btn-cta-outline { width: 100%; max-width: 320px; justify-content: center; }
  }
`;

